import React, { useState } from 'react';
import { ClipboardListIcon, SearchIcon, TrashIcon, UserIcon, ClockIcon, ActivityIcon, FilterIcon, ShieldAlertIcon, DownloadIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { usePermissions } from '../../hooks/usePermissions';
import { usePagination } from '../../hooks/usePagination';
import { AuditAction } from '../../types';
import { formatDate, formatTime, getModuleNameInSpanish } from '../../utils/helpers';
import { exportAuditLogsToExcel } from '../../utils/excelExport';
export function AuditLog() {
  const {
    auditLogs
  } = useData();
  const {
    canView
  } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  // Permission check
  if (!canView('auditLog')) {
    return <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6" data-id="element-275">
        <ShieldAlertIcon className="w-16 h-16 text-gray-300 mb-4" data-id="element-276" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-277">
          Acceso Restringido
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md" data-id="element-278">
          No tienes permisos para ver la bitácora de auditoría. Contacta al
          administrador si necesitas acceso.
        </p>
      </div>;
  }
  // Filter logs
  const filteredLogs = auditLogs.filter(log => {
    const matchesSearch = log.details.toLowerCase().includes(searchTerm.toLowerCase()) || log.userName.toLowerCase().includes(searchTerm.toLowerCase()) || log.entityName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesModule = !filterModule || log.module === filterModule;
    const matchesAction = !filterAction || log.action === filterAction;
    return matchesSearch && matchesModule && matchesAction;
  });
  // Pagination
  const {
    paginatedItems,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredLogs, {
    itemsPerPage: 10
  });
  // Options for selects
  const moduleOptions = Array.from(new Set(auditLogs.map(l => l.module))).map(m => ({
    value: m,
    label: getModuleNameInSpanish(m)
  }));
  const actionOptions = [{
    value: 'create',
    label: 'Creación'
  }, {
    value: 'update',
    label: 'Actualización'
  }, {
    value: 'delete',
    label: 'Eliminación'
  }, {
    value: 'complete',
    label: 'Completado'
  }, {
    value: 'cancel',
    label: 'Cancelación'
  }, {
    value: 'reschedule',
    label: 'Reprogramación'
  }];
  const getActionBadge = (action: AuditAction) => {
    switch (action) {
      case 'create':
        return <Badge variant="success" data-id="element-279">Creación</Badge>;
      case 'update':
        return <Badge variant="info" data-id="element-280">Edición</Badge>;
      case 'delete':
        return <Badge variant="danger" data-id="element-281">Eliminación</Badge>;
      case 'complete':
        return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" data-id="element-282">
            Completado
          </Badge>;
      case 'cancel':
        return <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400" data-id="element-283">
            Cancelado
          </Badge>;
      case 'reschedule':
        return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" data-id="element-284">
            Reprogramado
          </Badge>;
      default:
        return <Badge variant="default" data-id="element-285">{action}</Badge>;
    }
  };
  const handleClearLogs = () => {
    clearAuditLogs();
    setIsClearConfirmOpen(false);
  };
  return <div className="space-y-6" data-id="element-286">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-287">
        <div data-id="element-288">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-289">
            Bitácora de Auditoría
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-290">
            Registro de actividades y cambios en el sistema
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-291">
          {filteredLogs.length > 0 && <Button variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-800 dark:hover:bg-emerald-900/20" onClick={() => exportAuditLogsToExcel(filteredLogs)} leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-293" />} data-id="element-292">
              Exportar Excel
            </Button>}
        </div>
      </div>

      {/* Filters */}
      <Card data-id="element-294">
        <div className="flex flex-col lg:flex-row gap-4" data-id="element-295">
          <div className="flex-1 relative" data-id="element-296">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-297" />
            <Input placeholder="Buscar por usuario, entidad o detalles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-298" />
          </div>
          <div className="w-full lg:w-48" data-id="element-299">
            <Select options={[{
            value: '',
            label: 'Todos los módulos'
          }, ...moduleOptions]} value={filterModule} onChange={setFilterModule} placeholder="Módulo" data-id="element-300" />
          </div>
          <div className="w-full lg:w-48" data-id="element-301">
            <Select options={[{
            value: '',
            label: 'Todas las acciones'
          }, ...actionOptions]} value={filterAction} onChange={setFilterAction} placeholder="Acción" data-id="element-302" />
          </div>
        </div>
      </Card>

      {/* Logs Table */}
      <Card padding="none" data-id="element-303">
        <div className="overflow-x-auto" data-id="element-304">
          <table className="w-full" data-id="element-305">
            <thead data-id="element-306">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-307">
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-308">
                  Fecha y Hora
                </th>
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-309">
                  Usuario
                </th>
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-310">
                  Acción
                </th>
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-311">
                  Módulo
                </th>
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-312">
                  Detalles
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-313">
              {paginatedItems.length === 0 ? <tr data-id="element-314">
                  <td colSpan={5} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-315">
                    <div className="flex flex-col items-center justify-center" data-id="element-316">
                      <ClipboardListIcon className="w-12 h-12 text-gray-300 mb-3" data-id="element-317" />
                      <p data-id="element-318">No se encontraron registros</p>
                    </div>
                  </td>
                </tr> : paginatedItems.map(log => <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" data-id="element-319">
                    <td className="py-4 px-6 whitespace-nowrap" data-id="element-320">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-321">
                        <ClockIcon className="w-4 h-4 text-gray-400" data-id="element-322" />
                        <span data-id="element-323">
                          {formatDate(new Date(log.timestamp).toISOString())}
                          <span className="text-gray-400 mx-1" data-id="element-324">•</span>
                          {formatTime(new Date(log.timestamp).toTimeString().slice(0, 5))}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6 whitespace-nowrap" data-id="element-325">
                      <div className="flex items-center gap-2" data-id="element-326">
                        <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-900 flex items-center justify-center text-sky-600 dark:text-sky-400" data-id="element-327">
                          <UserIcon className="w-4 h-4" data-id="element-328" />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-329">
                          {log.userName}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6 whitespace-nowrap" data-id="element-330">
                      {getActionBadge(log.action)}
                    </td>
                    <td className="py-4 px-6 whitespace-nowrap" data-id="element-331">
                      <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-332">
                        {getModuleNameInSpanish(log.module)}
                      </span>
                    </td>
                    <td className="py-4 px-6" data-id="element-333">
                      <p className="text-sm text-gray-900 dark:text-gray-200" data-id="element-334">
                        {log.details}
                      </p>
                      {log.entityName && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-335">
                          Entidad: {log.entityName}
                        </p>}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 pb-4" data-id="element-336">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-337" />
        </div>
      </Card>

      {/* Clear Confirmation Modal */}
      <Modal isOpen={isClearConfirmOpen} onClose={() => setIsClearConfirmOpen(false)} title="Limpiar Bitácora" size="sm" data-id="element-338">
        <div className="space-y-4" data-id="element-339">
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg flex items-start gap-3" data-id="element-340">
            <ShieldAlertIcon className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" data-id="element-341" />
            <div data-id="element-342">
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-300" data-id="element-343">
                Acción Destructiva
              </h4>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-344">
                Estás a punto de eliminar permanentemente todos los registros de
                auditoría. Esta acción no se puede deshacer.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2" data-id="element-345">
            <Button variant="outline" onClick={() => setIsClearConfirmOpen(false)} data-id="element-346">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleClearLogs} data-id="element-347">
              Eliminar Todo
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}